import type { ReactNode } from "react";
import type { EnvironmentView, ScenePlan } from "@pushback-and-chaos/contracts";
import { EnvironmentSection } from "../../environment/section.js";
import { HeroPlate } from "../beat.js";
import { CoverPlate, CoverSprites } from "../../ui/case-card.js";

/**
 * THE SCENE — the upper right of the Storyteller composition.
 *
 * The live `EnvironmentSection` moves here whole. It is still the only thing
 * that draws the world, it still draws the server's `EnvironmentView` and the
 * turn's `ScenePlan`, and nothing in this file decides who is in the room,
 * which way they are facing, who is holding the folder, or how the place is
 * lit. This panel supplies a rectangle, a caption and a footer; the scene
 * authority is where it was.
 *
 * The framing is the section's own default — the room the learner is in — and
 * this panel offers no way out to the whole building (2026-09-18): at this
 * size the wide view is a drawing of a facade, and the room is the scene.
 *
 * The caption and footer are diegetic strings the view already carries —
 * `sceneLabel` and the authored `description`, both of which run through the
 * same leak scan as narration. The hour is the wall clock's own face, which
 * Law 2 admits as fiction; it is never a countdown in a costume.
 *
 * WHILE A PROLOGUE PAGE IS OPEN (2026-09-22) the stage shows that page's
 * authored hero instead — the same build-time plate the standalone prologue
 * drew — with the page's running label as its caption. The live view is not
 * discarded; it is simply behind the page, and comes back the moment the
 * reader turns into the story.
 *
 * ON THE TITLE PAGE it shows the case's own COVER (Codex, 2026-09-22): the
 * first prologue hero belongs to the first narrated moment, and borrowing
 * it for the title made turning the page repeat a beat. The cover is a
 * public-premise illustration, drawn as an inset frontispiece on the
 * scene's paper, never as a claim about where the learner is.
 */
export interface SceneHero {
  readonly caseSlug: string;
  /** The authored page's own index, as the bible numbers it. */
  readonly pageIndex: number;
  readonly slot: string;
  readonly caption: string;
  readonly foot: string;
}

export function ScenePanel({
  environment,
  plan,
  hero = null,
  titleCover = null,
}: {
  readonly environment: EnvironmentView | null;
  readonly plan: ScenePlan | null;
  readonly hero?: SceneHero | null;
  /** The case whose cover the title page shows; null on every other page. */
  readonly titleCover?: { readonly slug: string; readonly client: string } | null;
}): ReactNode {
  if (titleCover !== null) {
    return (
      <section className="pcs-panel pcs-scene" aria-label="The case illustration" data-scene="cover">
        <div className="pcs-scene-bar pcs-scene-caption">
          <span className="pcs-truncate">{titleCover.client}</span>
        </div>
        <div className="pcs-scene-stage">
          <CoverSprites slugs={[titleCover.slug]} />
          <CoverPlate slug={titleCover.slug} className="pcs-title-cover" />
        </div>
        <div className="pcs-scene-bar pcs-scene-foot" aria-hidden="true" />
      </section>
    );
  }

  if (hero !== null) {
    return (
      <section className="pcs-panel pcs-scene" aria-label="The scene" data-scene="prologue">
        <div className="pcs-scene-bar pcs-scene-caption">
          <span className="pcs-truncate">{hero.caption}</span>
        </div>
        <div className="pcs-scene-stage">
          <HeroPlate slot={hero.slot} caseSlug={hero.caseSlug} pageIndex={hero.pageIndex} />
        </div>
        <div className="pcs-scene-bar pcs-scene-foot">
          <span className="pcs-truncate">{hero.foot}</span>
        </div>
      </section>
    );
  }

  if (environment === null) {
    return (
      <section className="pcs-panel pcs-scene" aria-label="The scene">
        <div className="pcs-scene-bar pcs-scene-caption">
          <span className="pcs-truncate">Where you are</span>
        </div>
        <div className="pcs-scene-stage" />
        <div className="pcs-scene-bar pcs-scene-foot">
          <span className="pcs-truncate">The room has not been drawn for this moment.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="pcs-panel pcs-scene" aria-label="The scene">
      <div className="pcs-scene-bar pcs-scene-caption">
        <span className="pcs-truncate">{environment.sceneLabel}</span>
        <span className="pcs-truncate" style={{ fontSize: "9px", letterSpacing: ".5px" }}>
          {timeOfDay(environment.clock.hour)}
        </span>
      </div>
      <div className="pcs-scene-stage">
        <EnvironmentSection
          view={environment}
          plan={plan}
          // No framing buttons over the art, and none in the footer either.
          controls="none"
        />
      </div>
      <div className="pcs-scene-bar pcs-scene-foot">
        <span className="pcs-truncate">{environment.description}</span>
      </div>
    </section>
  );
}

/**
 * The hour as a person in the room would say it. Words, not a number, and
 * never a spend meter: the clock's face is fiction (Law 2's wall clock), and
 * this is the same fact in the same register as the rest of the caption.
 */
function timeOfDay(hour: number): string {
  if (hour < 5) return "the small hours";
  if (hour < 9) return "early morning";
  if (hour < 12) return "mid-morning";
  if (hour < 14) return "midday";
  if (hour < 16) return "early afternoon";
  if (hour < 18) return "late afternoon";
  if (hour < 21) return "evening";
  return "night";
}
